import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import { Notifications, ArrowBackIosNew as ArrowBackIosNewIcon } from '@mui/icons-material';
import { AnnouncementDetail as AnnouncementDetailType } from '../types';
import { announcementApi } from '../services/api';
import { useAuth } from '../auth/AuthContext';

const formatAnnouncementDate = (dateStr: string) => {
  try {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}.${m}.${d}`;
  } catch (e) {
    return dateStr;
  }
};

const AnnouncementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState<AnnouncementDetailType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { storeId } = useAuth();

  useEffect(() => {
    const fetchAnnouncement = async () => {
      if (!id) {
        setError('お知らせIDが指定されていません');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await announcementApi.getAnnouncementDetail(id, undefined, storeId);
        setAnnouncement(response);
        setError(null);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setError('このお知らせにアクセスする権限がありません。');
        } else if (err?.response?.status === 404) {
          setError('お知らせが見つかりません。');
        } else {
          setError('お知らせの取得に失敗しました。');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncement();
  }, [id, storeId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8f8f9' }}>
      {/* ヘッダー */}
      <Box sx={{ pt: 0, pb: { xs: 1, sm: 1.5 }, pl: 0, pr: { xs: 0, sm: 3 }, mx: { xs: -2, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: '#3c3c3c', p: 0.5, mr: 1.5, flexShrink: 0 }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#3c3c3c' }}>
            <Notifications sx={{ color: '#898989', fontSize: 22 }} />
            お知らせ
          </Typography>
        </Box>
      </Box>

      {/* ヘッダー下区切り線 */}
      <Box sx={{ height: '2px', bgcolor: '#e7e7e7', mx: { xs: -2, sm: -3 }, width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' } }} />

      {/* コンテンツ */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, mx: { xs: -2, sm: 0 } }}>
        {error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" onClick={() => navigate(-1)} size="small">
              戻る
            </Button>
          </Box>
        ) : !announcement ? (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ color: '#898989' }}>お知らせが見つかりません</Typography>
          </Box>
        ) : (
          <Box>
            {/* タイトルエリア */}
            <Box sx={{ py: 2.5 }}>
              {/* タイトル */}
              <Typography
                sx={{
                  fontSize: '17px',
                  fontWeight: 'bold',
                  color: '#3c3c3c',
                  lineHeight: 1.5,
                  mb: 1,
                }}
              >
                {announcement.title}
              </Typography>

              {/* 日付 */}
              <Typography sx={{ fontSize: '12px', color: '#898989' }}>
                {formatAnnouncementDate(announcement.created_at)}
              </Typography>
            </Box>

            {/* 本文エリア */}
            <Box sx={{ pt: 2 }}>
              {/* 画像 */}
              {announcement.image_url && (
                <Box sx={{ mb: 2.5, textAlign: 'center' }}>
                  <img
                    src={announcement.image_url}
                    alt={announcement.title}
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px' }}
                  />
                </Box>
              )}

              {/* 本文 */}
              <Typography
                sx={{
                  fontSize: '15px',
                  color: '#3c3c3c',
                  lineHeight: 1.8,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {announcement.content}
              </Typography>

              {/* リンク */}
              {announcement.link_url && (
                <Box sx={{ mt: 2.5 }}>
                  <Button
                    variant="outlined"
                    href={announcement.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ color: '#0071e3', borderColor: '#0071e3' }}
                  >
                    {announcement.link_text || 'リンクを開く'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AnnouncementDetail;
